import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import { useEffect, useState } from "react";
import MDButton from "components/MDButton";
import { Card, Grid, TextField, Autocomplete, Icon } from "@mui/material";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { DATE_RANGE_OPTIONS } from "components/common/constant";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import MasterUpdationBarChart from "examples/Charts/BarCharts/MasterUpdationBarChart";
import { adminDashboardData } from "actions/dashboard";

function AdminDashboard() {
  const { notify } = useNotify();
  const [adoptionRateChartData, setAdoptionRateChartData] = useState({
    labels: ["Adoption %"],
    datasets: [],
  });
  const [masterUpdationChartData, setMasterUpdationChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [roleMasterUpdateChartData, setRoleMasterUpdateChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [filters, setFilters] = useState(() => {
    const today = dayjs().format("YYYY-MM-DD");

    return {
      dateRange: "TODAY",
      startDate: today,
      endDate: today,
      isSearched: false,
    };
  });
  const [listState, setListState] = useState({
    showLoaderOnClick: false,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: filters.startDate,
    endDate: filters.endDate,
  });

  useEffect(() => {
    getDashboardData();
  }, []);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = dayjs(today).format("YYYY-MM-DD");

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = dayjs(endDate).format("YYYY-MM-DD");

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  };

  const getDashboardData = async (customPayload = null) => {
    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      const payload = customPayload || {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };

      const response = await adminDashboardData(payload);

      if (response?.code !== 200) {
        notify(response?.message || "Failed to fetch dashboard data", "error");
        return;
      }

      const adoptionData = response?.data?.adoptionRateReport || [];

      const colors = [
        "#156082",
        "#ED7D31",
        "#2E7D32",
        "#29B6F6",
        "#AB47BC",
        "#8BC34A",
        "#0D5C7D",
        "#F57C20",
        "#4CAF50",
        "#9C27B0",
        "#795548",
        "#607D8B",
      ];

      setAdoptionRateChartData({
        labels: adoptionData.map((item) => item.departmentName),
        datasets: [
          {
            label: "Adoption %",
            backgroundColor: adoptionData.map(
              (_, index) => colors[index % colors.length],
            ),
            data: adoptionData.map((item) => Number(item.adoptionRate)),
          },
        ],
      });

      const roleData = response?.data?.role_master_update_report || [];

      setRoleMasterUpdateChartData({
        labels: roleData.map((item) => item.role),
        datasets: [
          {
            label: "New",
            backgroundColor: "#1F77B4",
            data: roleData.map((item) => Number(item.createdCount || 0)),
          },
          {
            label: "Delete",
            backgroundColor: "#2CA02C",
            data: roleData.map((item) => Number(item.deletedCount || 0)),
          },
        ],
      });

      const masterData = response?.data?.master_updation_report || [];

      setMasterUpdationChartData({
        labels: masterData.map((item) => `${item.master}`),
        datasets: [
          {
            label: "New",
            backgroundColor: "#8BC34A",
            data: masterData.map((item) => Number(item.createdCount || 0)),
          },
          {
            label: "Delete",
            backgroundColor: "#03A9F4",
            data: masterData.map((item) => Number(item.deletedCount || 0)),
          },
        ],
      });
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const handleSearch = () => {
    setAppliedDateRange({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });

    getDashboardData();

    setFilters((prev) => ({
      ...prev,
      isSearched: true,
    }));
  };

  const handleClear = () => {
    const today = dayjs().format("YYYY-MM-DD");

    const resetFilters = {
      dateRange: "TODAY",
      startDate: today,
      endDate: today,
      isSearched: false,
    };

    setAppliedDateRange({
      startDate: resetFilters.startDate,
      endDate: resetFilters.endDate,
    });

    setFilters(resetFilters);

    getDashboardData({
      startDate: resetFilters.startDate,
      endDate: resetFilters.endDate,
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>
                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            minDate={dayjs(filters.startDate)}
                            label="End Date"
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={filters.dateRange === "CUSTOM" ? 2 : 3}
                    xl={2}
                  >
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      gap={1}
                      width="100%"
                      alignItems="flex-start"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          minWidth: "120px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.isSearched && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            minWidth: "120px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Grid container spacing={3} mt={0.3}>
          <Grid item xs={4}>
            <Card>
              <MDBox px={3} py={1.5}>
                <Typography variant="body2" fontWeight="bold">
                  {
                    DATE_RANGE_OPTIONS.find(
                      (item) => item.value === filters.dateRange,
                    )?.label
                  }
                </Typography>

                <Typography variant="caption" color="text">
                  {appliedDateRange.startDate === appliedDateRange.endDate
                    ? dayjs(appliedDateRange.startDate).format("DD-MMM-YYYY")
                    : `${dayjs(appliedDateRange.startDate).format("DD-MMM-YYYY")} to ${dayjs(
                        appliedDateRange.endDate,
                      ).format("DD-MMM-YYYY")}`}
                </Typography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <MasterUpdationBarChart
                  color="light"
                  title="Master Updation"
                  chart={masterUpdationChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <MasterUpdationBarChart
                  color="light"
                  title="Role Master Update"
                  chart={roleMasterUpdateChartData}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="light"
                  title="Adoption Rate"
                  chart={adoptionRateChartData}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

export default AdminDashboard;
